import { CommonModule, DatePipe, NgOptimizedImage } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { MatBadgeModule } from "@angular/material/badge";
import { MatBottomSheetModule } from "@angular/material/bottom-sheet";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MAT_DATE_LOCALE, MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
// MatAvatarModule não existe, vamos usar mat-icon para avatar
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatStepperModule } from "@angular/material/stepper";
import { MatSortModule } from "@angular/material/sort";
import { MatTableModule } from "@angular/material/table";
import { MatTabsModule } from "@angular/material/tabs";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgxChartsModule } from "@swimlane/ngx-charts";
import { LazyLoadImageModule } from "ng-lazyload-image";
import { NgxFileDropModule } from "ngx-file-drop";
import { IConfig, NgxMaskModule } from "ngx-mask";
import { ToastrModule } from "ngx-toastr";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { TableComponent } from "./components/table/table.component";
import { AuthInterceptorProvider } from "./helpers/auth.interceptor";
// Novos services
import { CalendarService } from "./services/calendar.service";
import { ModalService } from "./services/modal.service";
import { AppointmentStateService } from "./services/appointment-state.service";
import { TagManagementService } from "./services/tag-management.service";
import { ValidationService } from "./services/validation.service";
import { ErrorHandlerService } from "./services/error-handler.service";
// Novos componentes e serviços da Fase 3
import { ReusableCalendarComponent } from "./shared/components/reusable-calendar/reusable-calendar.component";
import { SkeletonLoadingComponent } from "./shared/components/skeleton-loading/skeleton-loading.component";
import { NotificationContainerComponent } from "./shared/components/notification-container/notification-container.component";
import { CacheService } from "./services/cache.service";
import { DebounceService } from "./services/debounce.service";
import { RoutinesOptimizationService } from "./services/routines-optimization.service";
import { AnimationService } from "./services/animation.service";
import { NotificationService } from "./services/notification.service";
// Novos serviços da Fase 4
import { PwaService } from "./services/pwa.service";
import { PerformanceService } from "./services/performance.service";
import { AnalyticsService } from "./services/analytics.service";
import { LazyLoadingService } from "./services/lazy-loading.service";
import { BundleOptimizationService } from "./services/bundle-optimization.service";
import { BackupService } from "./services/backup.service";
import { VirtualScrollComponent } from "./shared/components/virtual-scroll/virtual-scroll.component";
import { FirstAccessComponent } from "./features/first-access/components/first-access.component";
import { PersonAppointmentConfirmComponent } from "./pages/person/person-appointment/person-appointment-confirm/person-appointment-confirm.component";
import { PersonAppointmentDialogComponent } from "./pages/person/person-appointment/person-appointment-dialog/person-appointment-dialog.component";
import { RateLimitExceededComponent } from "./features/rate-limit-exceeded/rate-limit-exceeded.component";
import { CnpjPipe } from "./pipe/cnpj.pipe";
import { CpfPipe } from "./pipe/cpf.pipe";
import { PersonTypePipe } from "./pipe/person-type.pipe";
import { PhonePipe } from "./pipe/phone.pipe";
import { CommunicationComponent } from "./components/communication/communication.component";
import { PersonAppointmentTaskComponent } from "./pages/person/person-appointment/person-appointment-task/person-appointment-task.component";
import { NgChartsModule } from "ng2-charts";
import { EmployeeListComponent } from "./features/employees/components/employee-list/employee-list.component";
import { EmployeeFormComponent } from "./features/employees/components/employee-form/employee-form.component";
import { ZebraPersonTableComponent } from "./components/zebra-employee-table/zebra-employee-table.component";
import { SuppliersListComponent } from "./features/suppliers/components/suppliers-list/suppliers-list.component";
import { SupplierRoutingModule } from "./features/suppliers/supplier-routing.module";
import { SuppliersFormComponent } from "./features/suppliers/components/suppliers-form/suppliers-form.component";
import { ProfessionalsFormComponent } from "./features/professionals/components/professionals-form/professionals-form.component";
import { ProfessionalsListComponent } from "./features/professionals/components/professionals-list/professionals-list.component";
import { ProfessionalRoutingModule } from "./features/professionals/professional-routing.module";
import { GoalRoutingModule } from "./features/goals/goal-routing.module";
import { TasksListComponent } from "./features/tasks/components/tasks-list/tasks-list.component";
import { TaskRoutingModule } from "./features/tasks/task-routing.module";
import { EmployeeAppointmentComponent } from "./features/employees/components/employee-appointment/employee-appointment.component";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { DateFormatPipe } from "./pipe/date-format.pipe";
import { DashboardsComponent } from "./features/dashboards/components/dashboards.component";
import { DashboardRoutingModule } from "./features/dashboards/dashboard-routing.module";
import { GoalTaskTableComponent } from "./components/goal-task-table/goal-task-table.component";
import { ReportComponent } from "./features/reports/components/report.component";
import { ReportRoutingModule } from "./features/reports/report-routing.module";
import { SpinnerComponent } from "./components/spinner/spinner.component";
import { DocumentsRoutingModule } from "./features/documents/documents-routing.module";
import { RoutinesListComponent } from "./features/routines/components/routines-list/routines-list.component";
import { RoutinesRoutingModule } from "./features/routines/routines-routing.module";
import { ResponsibilitiesListComponent } from "./features/responsibilities/components/responsibilities-list/responsibilities-list.component";
import { ResponsibilitiesRoutingModule } from "./features/responsibilities/responsibilities-routing.module";
import { ResponsibilitiesTableComponent } from "./components/responsibilities-table/responsibilities-table.component";
import { ResponsibilitiesFormComponent } from "./features/responsibilities/components/responsibilities-form/responsibilities-form.component";
import { RoutinesFormComponent } from "./features/routines/components/routines-form/routines-form.component";
import { DeleteDialogComponent } from "./components/delete-dialog/delete-dialog.component";
import { SidenavComponent } from "./components/sidenav/sidenav.component";
import { AppLayoutComponent } from "./components/app-layout/app-layout.component";
import { ChangePasswordFormComponent } from "./features/change-password/components/change-password-form/change-password-form.component";
import { SettingsComponent } from "./features/change-password/components/settings/settings.component";
import { EmployeeRoutingModule } from "./features/employees/employee-routing.module";
import { ChangePasswordRoutingModule } from "./features/change-password/change-password-routing.module";
import { LayoutModule } from "@angular/cdk/layout";
import { StripePaymentComponent } from "./features/stripe/components/stripe-payment/stripe-payment.component";
import { StripeRoutingModule } from "./features/stripe/stripe-routing.module";
import { PaymentSuccessComponent } from "./features/stripe/components/payment-success/payment-success.component";
import { LandingPageComponent } from "./features/landing-page/components/landing-page.component";
import { FooterLandingPageComponent } from "./components/footer-landing-page/footer-landing-page.component";
import { HeaderLandingPageComponent } from "./components/header-landing-page/header-landing-page.component";
import { LoginComponent } from "./features/login/components/login.component";
import { NavbarComponent } from "./components/navbar/navbar.component";
import { SelectCompanyComponent } from "./features/select-company/components/select-company.component";
import { PageNotFoundComponent } from "./features/page-not-found/components/page-not-found.component";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { ValidationMessageComponent } from "./shared/validation-message.component";
import { AvatarComponent } from "./shared/components/avatar/avatar.component";
import { ManagerViewComponent as DocumentsManagerViewComponent } from "./features/documents/components/manager-view/manager-view.component";
import { UserViewComponent as DocumentsUserViewComponent } from "./features/documents/components/user-view/user-view.component";
import { DocumentDialogComponent } from "./features/documents/components/document-dialog/document-dialog.component";
import { DocumentViewerComponent } from "./features/documents/components/document-viewer/document-viewer.component";
import { DocumentReadStatusComponent } from "./features/documents/components/document-read-status/document-read-status.component";
import { ManagerHomeComponent } from "./features/dashboards/components/manager-home/manager-home.component";
import { UserHomeComponent } from "./features/dashboards/components/user-home/user-home.component";
import { FirstAccessRoutingModule } from "./features/first-access/first-access-routing.module";
import { ForgotPasswordComponent } from "./features/forgot-password/components/forgot-password.component";
import { ForgotPasswordRoutingModule } from "./features/forgot-password/forgot-password-routing.module";
import { SelectCompanyRoutingModule } from "./features/select-company/select-company-routing.module";
import { LoginRoutingModule } from "./features/login/login-routing.module";
import { LandingPageRoutingModule } from "./features/landing-page/landing-page-routing.module";
// Componente de modal de atestados
import { AbstinenceModalModule } from "./shared/components/abstinence-modal/abstinence-modal.module";
// Componente de modal de férias
import { VacationModalModule } from "./shared/components/vacation-modal/vacation-modal.module";
import { RoutinesTableComponent } from "./components/routines-table/routines-table.component";
import { PageNotFoundRoutingModule } from "./features/page-not-found/page-not-found-routing.module";
import { TasksTableComponent } from "./components/tasks-table/tasks-table.component";
import { TasksFormComponent } from "./features/tasks/components/tasks-form/tasks-form.component";
import { GoalsListComponent } from "@features/goals/components/goals-list/goals-list.component";
import { GoalsTableComponent } from "@components/goals-table/goals-table.component";
import { GoalsFormComponent } from "@features/goals/components/goals-form/goals-form.component";
import { ResetPasswordComponent } from "./features/reset-password/components/reset-password.component";
import { ResetPasswordRoutingModule } from "./features/reset-password/reset-password-routing.module";
// SuperAdminModule não deve ser importado aqui - é carregado via lazy loading

export const maskConfig: Partial<IConfig> = {
  validation: false,
};

@NgModule({
  declarations: [
    CnpjPipe,
    PhonePipe,
    CpfPipe,
    PersonTypePipe,
    DateFormatPipe,

    // Novos componentes da Fase 3
    ReusableCalendarComponent,
    SkeletonLoadingComponent,
    NotificationContainerComponent,
    // Novos componentes da Fase 4
    VirtualScrollComponent,

    AppComponent,
    FirstAccessComponent,
    PersonAppointmentDialogComponent,
    PersonAppointmentConfirmComponent,
    TableComponent,
    CommunicationComponent,
    PersonAppointmentTaskComponent,
    RateLimitExceededComponent,
    ZebraPersonTableComponent,
    EmployeeListComponent,
    EmployeeFormComponent,
    SuppliersListComponent,
    SuppliersFormComponent,
    ProfessionalsFormComponent,
    ProfessionalsListComponent,
    TasksListComponent,
    TasksTableComponent,
    TasksFormComponent,
    GoalsListComponent,
    GoalsTableComponent,
    GoalsFormComponent,
    EmployeeAppointmentComponent,
    DashboardsComponent,
    GoalTaskTableComponent,
    ReportComponent,
    SpinnerComponent,
    RoutinesListComponent,
    RoutinesTableComponent,
    ResponsibilitiesListComponent,
    ResponsibilitiesTableComponent,
    ResponsibilitiesFormComponent,
    DeleteDialogComponent,
    SidenavComponent,
    AppLayoutComponent,
    RoutinesFormComponent,
    ChangePasswordFormComponent,
    SettingsComponent,
    StripePaymentComponent,
    PaymentSuccessComponent,
    LandingPageComponent,
    HeaderLandingPageComponent,
    FooterLandingPageComponent,
    LoginComponent,
    NavbarComponent,
    SelectCompanyComponent,
    PageNotFoundComponent,
    ValidationMessageComponent,
    AvatarComponent,
    DocumentsManagerViewComponent,
    DocumentDialogComponent,
    DocumentViewerComponent,
    DocumentReadStatusComponent,
    DocumentsUserViewComponent,
    ManagerHomeComponent,
    UserHomeComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,

    EmployeeRoutingModule,
    SupplierRoutingModule,
    ProfessionalRoutingModule,
    GoalRoutingModule,
    TaskRoutingModule,
    DashboardRoutingModule,
    ReportRoutingModule,
    DocumentsRoutingModule,
    RoutinesRoutingModule,
    ResponsibilitiesRoutingModule,
    ChangePasswordRoutingModule,
    StripeRoutingModule,
    FirstAccessRoutingModule,
    ForgotPasswordRoutingModule,
    SelectCompanyRoutingModule,
    LoginRoutingModule,
    LandingPageRoutingModule,
    ResetPasswordRoutingModule,

    PageNotFoundRoutingModule,

    AppRoutingModule,

    BrowserAnimationsModule,
    CommonModule,
    HttpClientModule,
    RouterModule,
    MatFormFieldModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatSnackBarModule,
    MatToolbarModule,
    MatTooltipModule,
    MatSidenavModule,
    MatButtonModule,
    MatSelectModule,
    MatInputModule,
    MatRadioModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatListModule,
    MatCardModule,
    MatTabsModule,
    MatGridListModule,
    MatDialogModule,
    MatExpansionModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatButtonToggleModule,
    NgxFileDropModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatBadgeModule,
    NgxChartsModule,
    MatChipsModule,
    MatBottomSheetModule,
    LazyLoadImageModule,
    MatStepperModule,
    NgChartsModule,
    ToastrModule.forRoot({
      timeOut: 3000,
      closeButton: true,
      progressBar: true,
      progressAnimation: "increasing",
      tapToDismiss: true,
      positionClass: "toast-bottom-right",
    }),
    NgxMaskModule.forRoot(maskConfig),
    NgbModule,
    NgOptimizedImage,
    LayoutModule,
    NgxExtendedPdfViewerModule,

    // Componente de modal de atestados
    AbstinenceModalModule,
    // Componente de modal de férias
    VacationModalModule,
  ],
  providers: [
    AuthInterceptorProvider,
    DatePipe,
    { provide: MAT_DATE_LOCALE, useValue: "pt-BR" },
    // Novos services
    CalendarService,
    ModalService,
    AppointmentStateService,
    TagManagementService,
    ValidationService,
    ErrorHandlerService,
    // Novos serviços da Fase 3
    CacheService,
    DebounceService,
    RoutinesOptimizationService,
    AnimationService,
    NotificationService,
    // Novos serviços da Fase 4
    PwaService,
    PerformanceService,
    AnalyticsService,
    LazyLoadingService,
    BundleOptimizationService,
    BackupService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
